import React, { 
  useState, 
  useEffect, 
  useRef, 
  useCallback, 
  useMemo 
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, X, Heart, Share2, 
  ChevronLeft, ChevronRight,
  Building, DollarSign, Maximize, FileText,
  ExternalLink, Play, Pause, ZoomIn
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

const MainPropertySection = ({ 
  property, 
  formatPrice, 
  currentImageIndex, 
  handleFavorite, 
  handleShare: originalHandleShare,
  handlePrevImage,
  handleNextImage,
  setCurrentImageIndex,
  isFavorite,
  handleContact: externalHandleContact
}) => {
  const { user } = useAuth();
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [hoverState, setHoverState] = useState({
    image: false,
    buttons: false
  });
  const [isPaused, setIsPaused] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  
  // Track loaded images to prevent duplicate logs
  const loadedImagesRef = useRef(new Set());
  
  // Store previous index for logging only on change
  const prevIndexRef = useRef(currentImageIndex);

  // Memoize property images length
  const imagesLength = useMemo(() => 
    property?.imagenes?.length || 0, 
    [property.imagenes]
  );

  // Log images only once when property changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const imagesCount = property?.imagenes?.length || 0;
      console.log('Property images updated:', {
        count: imagesCount,
        urls: property?.imagenes
      });
    }
    
    // Reset the loaded images set when property changes
    loadedImagesRef.current = new Set();
  }, [property.id]);
  
  // Autoplay carousel effect
  useEffect(() => {
    let intervalId;
  
    if (!isPaused && !isImageExpanded && imagesLength > 1) {
      intervalId = setInterval(() => {
        setCurrentImageIndex((prevIndex) => {
          const nextIndex = prevIndex === imagesLength - 1 ? 0 : prevIndex + 1;
          return nextIndex;
        });
      }, 5000);
    }
  
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPaused, isImageExpanded, imagesLength, setCurrentImageIndex]);

  // Log image index changes ONLY when it actually changes
  useEffect(() => {
    if (prevIndexRef.current !== currentImageIndex) {
      prevIndexRef.current = currentImageIndex;
    }
  }, [currentImageIndex]);
  
  // Register contact click function
  const registerContactClick = useCallback(async () => {
    try {
      const propertyId = property.id;
      const token = localStorage.getItem('token');
      
      if (!token) return;

      try {
        await fetch('https://backend-production-1786.up.railway.app/api/register-contact-click', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            propertyId
          })
        });
      } catch (error) {
        console.warn('Error con /api/register-contact-click, intentando alternativa:', error);
        
        const isRental = property.operacion === 'alquiler';
        
        await fetch('https://backend-production-1786.up.railway.app/api/track-visit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            propertyId,
            elapsedTime: 0,
            contactClicked: true
          })
        });
      }
    } catch (error) {
      console.error('Error al registrar el clic de contacto:', error);
    }
  }, [property.id, property.operacion]);

  // Enhanced share function
  const handleShare = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      await registerContactClick();
      
      if (originalHandleShare) {
        originalHandleShare();
      } else {
        // Llamar directamente sin pasar el evento
        if (window.handleShareFromParent) {
          window.handleShareFromParent();
        }
      }
    } catch (error) {
      console.error('Error al compartir la propiedad:', error);
    }
  }, [originalHandleShare, registerContactClick]);

  // Handle favorite with proper event handling
  const handleFavoriteClick = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    handleFavorite();
  }, [handleFavorite]);

  // Contact handler
  const handleContact = useCallback(async () => {
    try {
      await registerContactClick();
    } catch (error) {
      console.error('Error al registrar clic de contacto:', error);
    }
    
    if (externalHandleContact) {
      externalHandleContact();
      return;
    }
    
    const section = document.getElementById('contacto');
    section?.scrollIntoView({ behavior: 'smooth' });
  }, [registerContactClick, externalHandleContact]);

  // Memoize title size class to prevent recalculations
  const titleSizeClass = useMemo(() => 
    property.titulo.length <= 30 
      ? 'text-5xl md:text-6xl lg:text-7xl' 
      : 'text-4xl md:text-5xl lg:text-6xl',
    [property.titulo.length]
  );

  const PropertyImage = React.memo(({ url, alt, className, onClick }) => {
    const [error, setError] = useState(false);
    const imageUrlRef = useRef(url);
  
    const getFullUrl = useCallback((imageUrl) => {
      if (!imageUrl) return '/placeholder-property.jpg';
      if (imageUrl.startsWith('http')) return imageUrl;
      return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
    }, []);
  
    const fullUrl = useMemo(() => getFullUrl(url), [url, getFullUrl]);
  
    useEffect(() => {
      if (imageUrlRef.current !== url) {
        setError(false);
        imageUrlRef.current = url;
      }
    }, [url]);
  
    const handleError = useCallback((e) => {
      setError(true);
      console.error('Error loading image:', {
        originalUrl: url,
        fullUrl: fullUrl,
        error: e
      });
    }, [url, fullUrl]);
  
    const handleLoad = useCallback(() => {
      const imageKey = fullUrl || 'placeholder';
      if (!loadedImagesRef.current.has(imageKey)) {
        loadedImagesRef.current.add(imageKey);
      }
      setLoadedImages(prev => new Set([...prev, imageKey]));
    }, [fullUrl]);

    const isLoaded = loadedImages.has(fullUrl);
  
    return (
      <img
        src={error ? '/placeholder-property.jpg' : fullUrl}
        alt={alt}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
        onClick={onClick}
        style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
      />
    );
  });
  
  PropertyImage.displayName = 'PropertyImage';

  const scrollToFeatures = useCallback(() => {
    const section = document.getElementById('caracteristicas');
    section?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20 pb-16 -mt-5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column - Enhanced Image Carousel (Cambiado de orden) */}
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="relative order-1 lg:order-1"
            >
              <div className="relative aspect-square overflow-hidden shadow-2xl dark:shadow-black/50
                           transform transition-all duration-500 
                           hover:shadow-[0_20px_50px_rgba(8,_112,_184,_0.7)] dark:hover:shadow-[0_20px_50px_rgba(59,_130,_246,_0.5)]"
                   onMouseEnter={() => {
                     setHoverState(prev => ({ ...prev, image: true }));
                     setIsPaused(true);
                   }}
                   onMouseLeave={() => {
                     setHoverState(prev => ({ ...prev, image: false }));
                     setIsPaused(false);
                   }}>
                
                {/* Loading State */}
                {!loadedImages.has(property.imagenes[currentImageIndex] ? 
                  (property.imagenes[currentImageIndex].startsWith('http') ? 
                    property.imagenes[currentImageIndex] : 
                    `${CLOUDFLARE_PROPERTIES_URL}/${property.imagenes[currentImageIndex]}`) : 
                  '/placeholder-property.jpg') && (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 dark:from-gray-700 dark:to-gray-800
                                flex items-center justify-center z-20">
                    <div className="w-8 h-8 border-2 border-blue-500 dark:border-blue-400 border-t-transparent 
                                  rounded-full animate-spin"></div>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  <motion.div 
                    key={currentImageIndex}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.6 }}
                    className="relative w-full h-full"
                  >
                    {/* Subtle overlays */}
                    <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 dark:from-black/50 to-transparent z-10" />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/30 dark:from-black/50 to-transparent z-10" />

                    <PropertyImage
                      url={property.imagenes[currentImageIndex]}
                      alt={`${property.titulo} - Imagen ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700
                                hover:scale-105 cursor-pointer"
                      onClick={() => {
                        setIsImageExpanded(true);
                        setIsPaused(true);
                      }}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Top Action Buttons */}
                <div className="absolute top-4 right-4 z-40 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleFavoriteClick}
                    className="group relative backdrop-blur-xl bg-white/80 dark:bg-white/10 p-3 rounded-xl
                             shadow-lg transition-all duration-300 
                             border border-white/50 dark:border-white/20 hover:border-blue-200 dark:hover:border-blue-500
                             touch-manipulation select-none hover:bg-white/90 dark:hover:bg-white/20"
                    style={{ 
                      pointerEvents: 'auto',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                  >
                    <Heart className={`w-5 h-5 transition-all duration-300 
                                    ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700 dark:text-gray-300'}`} />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleShare}
                    className="group relative backdrop-blur-xl bg-white/80 dark:bg-white/10 p-3 rounded-xl
                             shadow-lg transition-all duration-300 
                             border border-white/50 dark:border-white/20 hover:border-blue-200 dark:hover:border-blue-500
                             touch-manipulation select-none hover:bg-white/90 dark:hover:bg-white/20"
                    style={{ 
                      pointerEvents: 'auto',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                  >
                    <Share2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </motion.button>
                </div>

                {/* Navigation Buttons */}
                {imagesLength > 1 && (
                  <>
                    <motion.button
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ 
                        opacity: 1, 
                        x: 0 
                      }}
                      whileHover={{ scale: 1.1, x: -5 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-30
                               p-3 backdrop-blur-xl bg-white/80 dark:bg-white/10 rounded-xl text-gray-800 dark:text-gray-200
                               shadow-lg transition-all duration-300 hover:bg-white dark:hover:bg-white/20
                               border border-white/50 dark:border-white/20 hover:border-blue-200 dark:hover:border-blue-500
                               lg:opacity-0 lg:hover:opacity-100 
                               opacity-100 sm:opacity-100 touch-manipulation"
                      style={{ pointerEvents: 'auto' }}
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </motion.button>

                    <motion.button
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ 
                        opacity: 1, 
                        x: 0 
                      }}
                      whileHover={{ scale: 1.1, x: 5 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-30
                               p-3 backdrop-blur-xl bg-white/80 dark:bg-white/10 rounded-xl text-gray-800 dark:text-gray-200
                               shadow-lg transition-all duration-300 hover:bg-white dark:hover:bg-white/20
                               border border-white/50 dark:border-white/20 hover:border-blue-200 dark:hover:border-blue-500
                               lg:opacity-0 lg:hover:opacity-100 
                               opacity-100 sm:opacity-100 touch-manipulation"
                      style={{ pointerEvents: 'auto' }}
                    >
                      <ChevronRight className="w-6 h-6" />
                    </motion.button>
                  </>
                )}

                {/* Enhanced Image Indicators */}
                {imagesLength > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {property.imagenes.map((_, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        className={`group relative h-2 transition-all duration-300 rounded-full overflow-hidden
                                  hover:scale-110 touch-manipulation ${index === currentImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
                        style={{ pointerEvents: 'auto' }}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700
                                      transition-opacity duration-300
                                      ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}
                        />
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Image Counter */}
                {imagesLength > 1 && (
                  <div className="absolute top-4 left-4 z-20 px-3 py-1 backdrop-blur-xl bg-black/40 dark:bg-black/60
                                rounded-full border border-white/20 dark:border-white/30">
                    <span className="text-white text-sm font-medium">
                      {currentImageIndex + 1} / {imagesLength}
                    </span>
                  </div>
                )}

                {/* Autoplay Control */}
                {imagesLength > 1 && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPaused(!isPaused);
                    }}
                    className="absolute bottom-4 right-4 z-30 p-2 backdrop-blur-xl bg-black/40 dark:bg-black/60
                             rounded-full border border-white/20 dark:border-white/30 hover:bg-black/60 dark:hover:bg-black/80 transition-all duration-300
                             touch-manipulation"
                    style={{ pointerEvents: 'auto' }}
                  >
                    {isPaused ? (
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    ) : (
                      <Pause className="w-4 h-4 text-white" />
                    )}
                  </motion.button>
                )}

                {/* Touch/Swipe Area for Mobile */}
                <div 
                  className="absolute inset-0 z-10 touch-pan-x md:hidden"
                  onTouchStart={(e) => {
                    // Solo manejar si el touch no es en los botones de acción
                    const target = e.target;
                    if (target.closest('button') || target.closest('[role="button"]')) {
                      return;
                    }

                    const touch = e.touches[0];
                    const startX = touch.clientX;
                    const startY = touch.clientY;
                    const startTime = Date.now();
                    let hasMoved = false;
                    let swipeDetected = false;
                    
                    const handleTouchMove = (moveEvent) => {
                      if (swipeDetected) return;
                      
                      const currentTouch = moveEvent.touches[0];
                      const deltaX = currentTouch.clientX - startX;
                      const deltaY = currentTouch.clientY - startY;
                      const timeElapsed = Date.now() - startTime;
                      
                      // Solo considerar como swipe si es rápido y horizontal
                      if (timeElapsed < 300 && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
                        hasMoved = true;
                        swipeDetected = true;
                        
                        if (deltaX > 0) {
                          handlePrevImage();
                        } else {
                          handleNextImage();
                        }
                        
                        cleanup();
                      } else if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
                        hasMoved = true;
                      }
                    };
                    
                    const handleTouchEnd = (endEvent) => {
                      cleanup();
                      
                      // Solo expandir imagen si no hubo movimiento significativo
                      if (!hasMoved && !swipeDetected) {
                        const timeElapsed = Date.now() - startTime;
                        // Solo si fue un tap rápido
                        if (timeElapsed < 200) {
                          setIsImageExpanded(true);
                          setIsPaused(true);
                        }
                      }
                    };
                    
                    const cleanup = () => {
                      document.removeEventListener('touchmove', handleTouchMove);
                      document.removeEventListener('touchend', handleTouchEnd);
                    };
                    
                    // Usar passive: true para evitar warnings
                    document.addEventListener('touchmove', handleTouchMove, { passive: true });
                    document.addEventListener('touchend', handleTouchEnd, { passive: true });
                  }}
                  onClick={(e) => {
                    // Solo manejar click si no es en un botón
                    const target = e.target;
                    if (!target.closest('button') && !target.closest('[role="button"]')) {
                      setIsImageExpanded(true);
                      setIsPaused(true);
                    }
                  }}
                />
              </div>
            </motion.div>

            {/* Right Column - Property Details (Cambiado de orden) */}
            <motion.div 
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="lg:flex lg:flex-col lg:justify-center h-full order-2 lg:order-2"
            >
              {/* Location */}
              <div className="hidden lg:block mb-6">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center gap-2 px-4 py-2 backdrop-blur-xl bg-blue-50/80 dark:bg-blue-500/20 rounded-xl
                             text-blue-700 dark:text-blue-300 transform transition-all duration-300 border border-blue-100 dark:border-blue-500/30"
                >
                  <MapPin className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  <span className="font-medium">{property.ubicacion}</span>
                </motion.div>
              </div>

              {/* Title and Price */}
              <div className="space-y-8">
                <motion.h1 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className={`${titleSizeClass} font-bold 
                             bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300
                             bg-clip-text text-transparent
                             leading-tight`}
                >
                  {property.titulo}
                </motion.h1>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="space-y-2"
                >
                  <span className="inline-block px-4 py-1 backdrop-blur-xl bg-blue-100/80 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300
                                rounded-full text-sm font-medium border border-blue-200 dark:border-blue-500/30">
                    {property.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
                  </span>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                     S/ {formatPrice(property.precio_soles)}
                  </div>
                  <div className="text-xl text-gray-500 dark:text-gray-400">
                    USD {formatPrice(property.precio_dolares)}
                  </div>
                  {property.operacion === 'alquiler' && property.mantenimiento > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="text-sm font-medium backdrop-blur-xl bg-gray-100/80 dark:bg-white/10 px-3 py-1 rounded-full border border-gray-200 dark:border-white/20">
                        Mantenimiento: S/ {formatPrice(property.mantenimiento)}
                      </span>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 mt-8"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleContact}
                  className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600 text-white
                           px-10 py-4 rounded-xl font-medium 
                           transform transition-all duration-500 ease-out
                           hover:shadow-2xl hover:from-blue-700 hover:to-blue-800 dark:hover:from-blue-600 dark:hover:to-blue-700
                           w-full sm:w-auto"
                >
                  <div className="relative z-10 flex items-center justify-center gap-2">
                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      Contactar Ahora
                    </span>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToFeatures}
                  className="backdrop-blur-xl bg-white/80 dark:bg-white/10 text-gray-800 dark:text-gray-200 px-10 py-4 rounded-xl font-medium 
                           transform transition-all duration-500 ease-out
                           hover:shadow-2xl hover:border-blue-200 dark:hover:border-blue-500
                           w-full sm:w-auto border border-gray-200 dark:border-white/20 hover:bg-white/90 dark:hover:bg-white/20"
                >
                  Ver Detalles
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Expanded Image Modal */}
      <AnimatePresence>
        {isImageExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 dark:bg-black/98 backdrop-blur-sm flex items-center justify-center"
            onClick={() => {
              setIsImageExpanded(false);
              setIsPaused(false);
            }}
          >
            {/* Close button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setIsImageExpanded(false);
                setIsPaused(false);
              }}
              className="absolute top-6 right-6 z-50 p-3 backdrop-blur-xl bg-black/50 dark:bg-black/70 rounded-full 
                       border border-white/20 dark:border-white/30 hover:bg-black/70 dark:hover:bg-black/90 transition-all duration-300"
            >
              <X className="w-6 h-6 text-white" />
            </motion.button>

            {/* Image container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full max-w-7xl mx-auto p-6 flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <PropertyImage
                url={property.imagenes[currentImageIndex]}
                alt={`${property.titulo} - Imagen ${currentImageIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain shadow-2xl dark:shadow-black/80"
              />
              
              {/* Navigation in modal */}
              {imagesLength > 1 && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1, x: -5 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    className="absolute left-8 top-1/2 -translate-y-1/2 p-4 backdrop-blur-xl bg-black/50 dark:bg-black/70
                             rounded-full border border-white/20 dark:border-white/30 hover:bg-black/70 dark:hover:bg-black/90 transition-all duration-300"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1, x: 5 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-4 backdrop-blur-xl bg-black/50 dark:bg-black/70
                             rounded-full border border-white/20 dark:border-white/30 hover:bg-black/70 dark:hover:bg-black/90 transition-all duration-300"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </motion.button>
                </>
              )}

              {/* Image indicators in modal */}
              {imagesLength > 1 && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {property.imagenes.map((_, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-300
                                ${index === currentImageIndex 
                                  ? 'bg-white' 
                                  : 'bg-white/50 hover:bg-white/70'}`}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(MainPropertySection);